import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import eyeLogo from '../assets/relationprotector-eye.svg';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const selectedRole = localStorage.getItem('selectedRole');
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', password: '', role: selectedRole || 'parent', parentCode: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginForm.email, loginForm.password);
      if (selectedRole && result?.user?.role !== selectedRole) {
        toast.error(`This account is ${result?.user?.role}. Please choose ${result?.user?.role} on role selection.`);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedRole');
        navigate('/');
        return;
      }
      toast.success('Welcome back!');
      navigate(result?.user?.role === 'child' ? '/child' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(regForm);
      toast.success('Account created!');
      navigate(result?.user?.role === 'child' ? '/child' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  const effectiveRole = selectedRole || regForm.role;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon"><img src={eyeLogo} alt="RelationProtector" className="brand-logo-img" /></div>
          <h1>RelationProtector</h1>
          <p>Parental Control & Child Safety</p>
        </div>

        <div className="auth-tabs">
          <button className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => setTab('login')}>Sign In</button>
          <button className={`auth-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => setTab('register')}>Create Account</button>
        </div>

        {tab === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" placeholder="parent@email.com" required
                value={loginForm.email} onChange={e => setLoginForm(p => ({...p, email: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" placeholder="••••••••" required
                value={loginForm.password} onChange={e => setLoginForm(p => ({...p, password: e.target.value}))} />
            </div>
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}>
              {loading ? '⏳ Signing in...' : '🔐 Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" type="text" placeholder="Your name" required
                value={regForm.name} onChange={e => setRegForm(p => ({...p, name: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" placeholder="your@email.com" required
                value={regForm.email} onChange={e => setRegForm(p => ({...p, email: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" placeholder="Min 6 characters" required minLength={6}
                value={regForm.password} onChange={e => setRegForm(p => ({...p, password: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Type</label>
              <input
                className="form-input"
                value={effectiveRole === 'child' ? '👦 Child Device Account' : '👨‍👩‍👧 Parent Account'}
                readOnly
              />
            </div>
            {effectiveRole === 'child' && (
              <div className="form-group">
                <label className="form-label">Parent Code (Parent's User ID)</label>
                <input className="form-input" type="text" placeholder="Get this from parent's dashboard"
                  value={regForm.parentCode} onChange={e => setRegForm(p => ({...p, parentCode: e.target.value, role: 'child'}))} />
              </div>
            )}
            <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}>
              {loading ? '⏳ Creating...' : '✨ Create Account'}
            </button>
          </form>
        )}

        <div className="auth-divider">Secure end-to-end encrypted connection</div>
        <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
          🔒 All monitoring requires explicit consent from the child device
        </div>
      </div>
    </div>
  );
}
